import React, { useState, useEffect, useMemo, useCallback } from "react";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import IconButton from "@material-ui/core/IconButton";
import CircularProgress from "@material-ui/core/CircularProgress";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import { makeStyles } from "@material-ui/core";

import ButtonWithSpinner from "../ButtonWithSpinner";
import AgendamentoTicketsList from "../AgendamentoTicketsList";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { BotaoConfirmar } from "../BotoesModal";

const useStyles = makeStyles((theme) => ({
    dialogPaper: {
        width: "90%",
        maxWidth: "1400px",
        height: "85vh",
        maxHeight: "85vh",
    },
    dialogContent: {
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
    },
    contentContainer: {
        display: "flex",
        gap: theme.spacing(2),
        marginTop: theme.spacing(1),
        flex: 1,
        minHeight: 0,
    },
    calendarContainer: {
        flex: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        minWidth: 0,
        minHeight: 0,
    },
    monthNav: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: theme.spacing(1),
        marginBottom: theme.spacing(1),
        flexShrink: 0,
    },
    monthLabel: {
        fontSize: 16,
        fontWeight: 600,
        textTransform: "capitalize",
        minWidth: 180,
        textAlign: "center",
    },
    calendarWrapper: {
        flex: 1,
        width: "100%",
        maxWidth: 700,
        minHeight: 0,
        position: "relative",
    },
    calendarScroll: {
        width: "100%",
        height: "100%",
        overflowY: "auto",
        ...theme.scrollbarStyles,
    },
    calendar: {
        borderCollapse: "collapse",
        width: "100%",
        height: "100%",
        minHeight: 420,
        textAlign: "center",
        tableLayout: "fixed",
    },
    calendarHeader: {
        backgroundColor: "#1976d2",
        color: "white",
        padding: 6,
        fontSize: 14,
        height: 32,
    },
    calendarDay: {
        border: "1px solid #ccc",
        padding: 4,
        cursor: "pointer",
        position: "relative",
        fontSize: 14,
        verticalAlign: "top",
        userSelect: "none",
    },
    calendarDayEmpty: {
        border: "1px solid #eee",
        backgroundColor: "#fafafa",
    },
    todayDay: {
        outline: "2px solid #1976d2",
        outlineOffset: "-2px",
    },
    selectedDay: {
        backgroundColor: "#90caf9",
        color: "#000",
        fontWeight: "bold",
    },
    eventBadge: {
        marginTop: 4,
        backgroundColor: "#e3f2fd",
        color: "#1565c0",
        borderRadius: 4,
        padding: "2px 6px",
        fontSize: 13,
        fontWeight: 600,
        display: "inline-block",
    },
    loadingOverlay: {
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "rgba(255, 255, 255, 0.6)",
        zIndex: 2,
        pointerEvents: "none",
    },
    ticketsContainer: {
        flex: 1,
        maxWidth: 520,
        display: "flex",
        flexDirection: "column",
        border: "1px solid rgba(0, 0, 0, 0.12)",
        borderRadius: 4,
        overflow: "hidden",
        minHeight: 0,
    },
}));

const MONTH_NAMES = [
    "janeiro", "fevereiro", "março", "abril", "maio", "junho",
    "julho", "agosto", "setembro", "outubro", "novembro", "dezembro",
];

const pad2 = (n) => String(n).padStart(2, "0");
const toIsoDate = (year, monthIndex, day) => `${year}-${pad2(monthIndex + 1)}-${pad2(day)}`;

const TicketAgendamentos = ({
    modalOpen,
    onClose,
    searchParam,
    selectedQueueIds,
    selectedWhatsappIds,
    selectedUserIds,
    selectedFilialIds,
    selectedTime,
    selectedTag,
    dataInicio,
    dataFim,
}) => {
    const classes = useStyles();
    const today = useMemo(() => new Date(), []);
    const [viewYear, setViewYear] = useState(today.getFullYear());
    const [viewMonth, setViewMonth] = useState(today.getMonth());
    const [selectedDate, setSelectedDate] = useState(null);
    const [commitments, setCommitments] = useState({});
    const [loadingCalendar, setLoadingCalendar] = useState(false);

    useEffect(() => {
        if (!modalOpen) return;
        setViewYear(today.getFullYear());
        setViewMonth(today.getMonth());
        setSelectedDate(null);
        setCommitments({});
    }, [modalOpen, today]);

    useEffect(() => {
        if (!modalOpen) return;
        if (!selectedWhatsappIds || selectedWhatsappIds.length === 0) {
            setCommitments({});
            return;
        }
        const monthStart = toIsoDate(viewYear, viewMonth, 1);
        const lastDay = new Date(viewYear, viewMonth + 1, 0).getDate();
        const monthEnd = toIsoDate(viewYear, viewMonth, lastDay);

        let cancelled = false;
        setLoadingCalendar(true);
        (async () => {
            try {
                const { data } = await api.get("/whatsappagendamentos", {
                    params: {
                        whatsappIds: JSON.stringify(selectedWhatsappIds),
                        queueIds: JSON.stringify(selectedQueueIds),
                        selectedTime,
                        selectedTag,
                        dataInicio,
                        dataFim,
                        monthStart,
                        monthEnd,
                    },
                });
                if (cancelled) return;
                const map = {};
                (data || []).forEach((item) => {
                    if (!item.data) return;
                    map[item.data] = Number(item.qtd) || 0;
                });
                setCommitments(map);
            } catch (err) {
                if (!cancelled) toastError(err);
            } finally {
                if (!cancelled) setLoadingCalendar(false);
            }
        })();

        return () => { cancelled = true; };
    }, [modalOpen, viewYear, viewMonth, selectedWhatsappIds, selectedQueueIds, selectedTime, selectedTag, dataInicio, dataFim]);

    const goPrevMonth = useCallback(() => {
        setSelectedDate(null);
        setViewMonth((m) => {
            if (m === 0) {
                setViewYear((y) => y - 1);
                return 11;
            }
            return m - 1;
        });
    }, []);

    const goNextMonth = useCallback(() => {
        setSelectedDate(null);
        setViewMonth((m) => {
            if (m === 11) {
                setViewYear((y) => y + 1);
                return 0;
            }
            return m + 1;
        });
    }, []);

    const handleDayClick = (day) => {
        if (!day) return;
        const iso = toIsoDate(viewYear, viewMonth, day);
        setSelectedDate(iso);
    };

    const renderCalendar = () => {
        const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
        const firstDayOfMonth = new Date(viewYear, viewMonth, 1).getDay();
        const daysArray = Array.from({ length: daysInMonth }, (_, i) => i + 1);
        const weeks = [];
        let week = new Array(firstDayOfMonth).fill(null);
        daysArray.forEach((day) => {
            week.push(day);
            if (week.length === 7) {
                weeks.push(week);
                week = [];
            }
        });
        if (week.length > 0) {
            while (week.length < 7) week.push(null);
            weeks.push(week);
        }

        const isToday = (day) =>
            day &&
            viewYear === today.getFullYear() &&
            viewMonth === today.getMonth() &&
            day === today.getDate();

        return (
            <table className={classes.calendar}>
                <thead>
                    <tr>
                        {["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"].map((d) => (
                            <th key={d} className={classes.calendarHeader}>{d}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {weeks.map((week, i) => (
                        <tr key={i}>
                            {week.map((day, idx) => {
                                if (!day) {
                                    return <td key={idx} className={classes.calendarDayEmpty} />;
                                }
                                const iso = toIsoDate(viewYear, viewMonth, day);
                                const qtd = commitments[iso];
                                const isSelected = selectedDate === iso;
                                const cellClasses = [classes.calendarDay];
                                if (isSelected) cellClasses.push(classes.selectedDay);
                                else if (isToday(day)) cellClasses.push(classes.todayDay);
                                return (
                                    <td
                                        key={idx}
                                        className={cellClasses.join(" ")}
                                        onClick={() => handleDayClick(day)}
                                    >
                                        <div>{day}</div>
                                        {qtd > 0 && (
                                            <span className={classes.eventBadge}>{qtd}</span>
                                        )}
                                    </td>
                                );
                            })}
                        </tr>
                    ))}
                </tbody>
            </table>
        );
    };

    const handleClose = () => {
        onClose();
    };

    return (
        <Dialog
            open={modalOpen}
            onClose={handleClose}
            maxWidth="xl"
            fullWidth
            disableBackdropClick
            classes={{ paper: classes.dialogPaper }}
        >
            <DialogTitle id="form-dialog-title">Agendamentos</DialogTitle>
            <DialogContent dividers className={classes.dialogContent}>
                <div className={classes.contentContainer}>
                    <div className={classes.calendarContainer}>
                        <div className={classes.monthNav}>
                            <IconButton size="small" onClick={goPrevMonth} aria-label="Mês anterior">
                                <ChevronLeftIcon />
                            </IconButton>
                            <span className={classes.monthLabel}>
                                {MONTH_NAMES[viewMonth]} {viewYear}
                            </span>
                            <IconButton size="small" onClick={goNextMonth} aria-label="Próximo mês">
                                <ChevronRightIcon />
                            </IconButton>
                        </div>
                        <div className={classes.calendarWrapper}>
                            <div className={classes.calendarScroll}>
                                {renderCalendar()}
                            </div>
                            {loadingCalendar && (
                                <div className={classes.loadingOverlay}>
                                    <CircularProgress />
                                </div>
                            )}
                        </div>
                    </div>
                    <div className={classes.ticketsContainer}>
                        <AgendamentoTicketsList
                            dataAgendamento={selectedDate}
                            searchParam={searchParam}
                            selectedQueueIds={selectedQueueIds}
                            selectedWhatsappIds={selectedWhatsappIds}
                            selectedUserIds={selectedUserIds}
                            selectedFilialIds={selectedFilialIds}
                            selectedTime={selectedTime}
                            selectedTag={selectedTag}
                            dataInicio={dataInicio}
                            dataFim={dataFim}
                        />
                    </div>
                </div>
            </DialogContent>
            <DialogActions>
                <BotaoConfirmar type="button" onClick={handleClose}>
                    Ok
                </BotaoConfirmar>
            </DialogActions>
        </Dialog>
    );
};

export default TicketAgendamentos;
